import React, { useState, useRef, useEffect } from 'react';
import { X, Loader, Search, Check, AlertCircle, ArrowLeft, Link, Image as ImageIcon, Upload, Maximize2 } from 'lucide-react';
import { useCategories } from '../../context/CategoryContext';
import CategorySelect from '../CategorySelect';
import { getAuthHeaders } from '../../context/AuthContext';
import { compressImage } from '../../lib/imageUtils';
import ProcessingDialog from './ProcessingDialog';
import SentimentDialog from './SentimentDialog';
import SentimentSection from './SentimentSection';
import TagSection from './TagSection';
import { resolveImageUrl } from '../../utils/imageUrl';
import { matchCategoryFromName } from '../../utils/categoryMatcher';
import { INITIAL_STASH_FORM } from '../../utils/stashFormDefaults';
import { showDetectionErrors } from '../../utils/detectionErrors';
import EditItemForm from './EditItemForm';

import { BACKEND_URL } from '../../config/api';

const URLLookupFlow = ({ isOpen, onClose, onSuccess }) => {
  const [step, setStep] = useState('input'); // 'input' | 'results' | 'form'

  // URL input state
  const [urlInput, setUrlInput] = useState('');
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupError, setLookupError] = useState(null);
  const [lookupResult, setLookupResult] = useState(null);

  // Image selection state
  const [selectedImage, setSelectedImage] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);

  // Form state
  const [formData, setFormData] = useState(INITIAL_STASH_FORM);
  const [imagePreview, setImagePreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isAddingSentiment, setIsAddingSentiment] = useState(false);
  const [isAddingTag, setIsAddingTag] = useState(false);

  // Processing dialog state
  const [showProcessing, setShowProcessing] = useState(false);
  const [detectColors, setDetectColors] = useState(true);
  const [detectSentiments, setDetectSentiments] = useState(true);
  const [showSentimentConfirm, setShowSentimentConfirm] = useState(false);
  const [pendingSentiments, setPendingSentiments] = useState([]);
  const [imageUploadError, setImageUploadError] = useState(null);
  const [showNoImageConfirm, setShowNoImageConfirm] = useState(false);

  const urlInputRef = useRef(null);

  useEffect(() => {
    if (!isOpen) {
      const timer = setTimeout(resetAll, 100);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  useEffect(() => {
    if (step === 'input' && urlInputRef.current) {
      urlInputRef.current.focus();
    }
  }, [step]);

  const resetAll = () => {
    setStep('input');
    setUrlInput('');
    setLookupLoading(false);
    setLookupError(null);
    setLookupResult(null);
    setSelectedImage(null);
    setPreviewUrl(null);
    setFormData(INITIAL_STASH_FORM);
    setImagePreview(null);
    setLoading(false);
    setUploadingImage(false);
    setSelectedFile(null);
    setShowProcessing(false);
    setDetectColors(true);
    setDetectSentiments(true);
    setShowSentimentConfirm(false);
    setPendingSentiments([]);
    setImageUploadError(null);
    setShowNoImageConfirm(false);
  };

  // === STEP 1: URL INPUT & LOOKUP ===
  const handleLookup = async () => {
    const url = urlInput.trim();
    if (!url) return;

    setLookupLoading(true);
    setLookupError(null);
    setLookupResult(null);

    try {
      const response = await fetch(`${BACKEND_URL}/api/stash/lookup-url`, {
        method: 'POST',
        credentials: 'include',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ url })
      });

      if (!response.ok) {
        const err = await response.clone().json();
        throw new Error(err.detail || 'Failed to fetch URL');
      }

      const data = await response.clone().json();

      if (!data.found) {
        setLookupError('No product information found at this URL. You can try a different URL or add the item manually.');
        return;
      }

      setLookupResult(data);

      // Pre-fill form with scraped product info
      const product = data.product || {};
      const productName = product.name || '';
      
      // Try to match category from product name if not provided
      let categoryValue = product.category || '';
      if (!categoryValue && productName) {
        categoryValue = matchCategoryFromName(productName);
      }
      
      setFormData(prev => ({
        ...prev,
        name: productName,
        brand: product.brand || '',
        description: product.description || '',
        category: categoryValue,
        tags: product.tags || [],
      }));

      // If backend already uploaded an image (direct image URL case)
      if (data.uploaded_image_url) {
        setFormData(prev => ({
          ...prev,
          image_url: data.uploaded_image_url,
          thumbnail_url: data.uploaded_image_url,
        }));
        setImagePreview(resolveImageUrl(data.uploaded_image_url));
      }

      // Auto-select the first available image
      if (data.images?.length > 0 && !data.uploaded_image_url) {
        setSelectedImage(data.images[0]);
      }

      setStep('results');
    } catch (error) {
      console.error('URL lookup error:', error);
      setLookupError(error.message || 'Failed to look up URL. Please try again.');
    } finally {
      setLookupLoading(false);
    }
  };

  // === STEP 2: RESULTS → SELECT IMAGE → FORM ===
  const handleSelectImage = (img) => {
    setSelectedImage(prev => prev?.url === img.url ? null : img);
  };

  const proceedToForm = async () => {
    // If no image selected and none already uploaded, confirm with user
    if (!selectedImage && !formData.image_url) {
      setShowNoImageConfirm(true);
      return;
    }
    await _proceedToFormInternal();
  };

  const _proceedToFormInternal = async () => {
    setShowNoImageConfirm(false);
    // If an external image was selected, download it
    if (selectedImage && !formData.image_url) {
      await downloadProductImage(selectedImage.url);
    }
    // Show processing options if we have an image
    if (formData.image_url || selectedImage) {
      setShowProcessing(true);
    }
    setStep('form');
  };

  const skipToManualEntry = () => {
    setStep('form');
  };

  const downloadProductImage = async (externalImageUrl) => {
    setUploadingImage(true);
    setImageUploadError(null);
    try {
      const fd = new FormData();
      fd.append('image_url', externalImageUrl);
      fd.append('detect_mode', 'none');

      const response = await fetch(`${BACKEND_URL}/api/stash/download-image`, {
        method: 'POST',
        credentials: 'include',
        headers: getAuthHeaders(),
        body: fd
      });

      if (response.ok) {
        const data = await response.clone().json();
        setFormData(prev => ({ ...prev, image_url: data.image_url, thumbnail_url: data.image_url }));
        setImagePreview(resolveImageUrl(data.image_url));
      } else {
        setImageUploadError('Could not download the product image. You can add one manually.');
      }
    } catch (error) {
      console.error('Image download error:', error);
      setImageUploadError('Image download failed. You can add an image manually.');
    } finally {
      setUploadingImage(false);
    }
  };

  // === IMAGE HANDLERS (for manual upload in form step) ===
  const handleFileSelect = async (file) => {
    if (!file || !file.type.startsWith('image/')) return;
    setImageUploadError(null);
    try {
      const compressed = await compressImage(file);
      const compressedFile = new File([compressed], file.name || 'image.jpg', { type: 'image/jpeg' });
      setSelectedFile(compressedFile);
      setImagePreview(URL.createObjectURL(compressed));
      setShowProcessing(true);
    } catch {
      setSelectedFile(file);
      setImagePreview(URL.createObjectURL(file));
      setShowProcessing(true);
    }
  };

  const handleChangeImage = () => {
    setImagePreview(null);
    setSelectedFile(null);
    setShowProcessing(false);
    setFormData(prev => ({ ...prev, image_url: '', thumbnail_url: '', colors: [] }));
  };

  // === PROCESSING HANDLERS ===
  const handleProcessImage = async () => {
    if (selectedFile) {
      await processManualImage();
    } else if (formData.image_url) {
      await processDownloadedImage();
    }
  };

  const processManualImage = async () => {
    setUploadingImage(true);
    try {
      const uploadData = new FormData();
      uploadData.append('file', selectedFile);
      let detectMode = 'none';
      if (detectColors && detectSentiments) detectMode = 'both';
      else if (detectColors) detectMode = 'color';
      else if (detectSentiments) detectMode = 'sentiment';
      uploadData.append('detect_mode', detectMode);

      const response = await fetch(`${BACKEND_URL}/api/stash/upload-image`, {
        method: 'POST', credentials: 'include', headers: getAuthHeaders(), body: uploadData
      });

      if (response.ok) {
        const data = await response.clone().json();
        showDetectionErrors(data);
        setFormData(prev => ({
          ...prev, image_url: data.image_url, thumbnail_url: data.thumbnail_url,
          colors: data.colors || [], primary_color: data.primary_color || '', secondary_color: data.secondary_color || ''
        }));
        setShowProcessing(false);
        if (detectSentiments && data.sentiments?.length > 0) {
          setPendingSentiments(data.sentiments);
          setShowSentimentConfirm(true);
        }
      } else {
        setShowProcessing(false);
        setImageUploadError('Image upload failed. Try a smaller image or different format.');
      }
    } catch (error) {
      console.error('Image upload error:', error);
      setShowProcessing(false);
      setImageUploadError('Image upload failed. Please check your connection and try again.');
    } finally {
      setUploadingImage(false);
    }
  };

  const processDownloadedImage = async () => {
    setUploadingImage(true);
    try {
      let detectMode = 'none';
      if (detectColors && detectSentiments) detectMode = 'both';
      else if (detectColors) detectMode = 'color';
      else if (detectSentiments) detectMode = 'sentiment';

      const fd = new FormData();
      fd.append('image_url', formData.image_url);
      fd.append('detect_mode', detectMode);

      const response = await fetch(`${BACKEND_URL}/api/stash/process-image`, {
        method: 'POST', credentials: 'include', headers: getAuthHeaders(), body: fd
      });

      if (response.ok) {
        const data = await response.clone().json();
        showDetectionErrors(data);
        setFormData(prev => ({
          ...prev, image_url: data.image_url, thumbnail_url: data.thumbnail_url,
          colors: data.colors || [], primary_color: data.primary_color || '', secondary_color: data.secondary_color || ''
        }));
        setImagePreview(resolveImageUrl(data.image_url));
        setShowProcessing(false);
        if (detectSentiments && data.sentiments?.length > 0) {
          setPendingSentiments(data.sentiments);
          setShowSentimentConfirm(true);
        }
      } else {
        setShowProcessing(false);
        setImageUploadError('Image processing failed.');
      }
    } catch (error) {
      console.error('Image processing error:', error);
      setShowProcessing(false);
      setImageUploadError('Image processing failed.');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSkipProcessing = () => {
    if (selectedFile) {
      uploadWithoutProcessing();
    } else {
      setShowProcessing(false);
    }
  };

  const uploadWithoutProcessing = async () => {
    if (!selectedFile) { setShowProcessing(false); return; }
    setUploadingImage(true);
    try {
      const uploadData = new FormData();
      uploadData.append('file', selectedFile);
      uploadData.append('detect_mode', 'none');
      const response = await fetch(`${BACKEND_URL}/api/stash/upload-image`, {
        method: 'POST', credentials: 'include', headers: getAuthHeaders(), body: uploadData
      });
      if (response.ok) {
        const data = await response.clone().json();
        setFormData(prev => ({ ...prev, image_url: data.image_url, thumbnail_url: data.thumbnail_url }));
      }
    } catch (error) {
      console.error('Image upload error:', error);
    } finally {
      setUploadingImage(false);
      setShowProcessing(false);
    }
  };

  const handleConfirmSentiments = () => {
    setFormData(prev => ({ ...prev, sentiments: [...prev.sentiments, ...pendingSentiments] }));
    setPendingSentiments([]);
    setShowSentimentConfirm(false);
  };

  // === SUBMIT ===
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.category) {
      alert('Please select a category');
      return;
    }
    
    setLoading(true);

    try {
      const submitData = new FormData();
      Object.keys(formData).forEach(key => {
        if (key === 'colors' && formData.colors.length > 0) {
          submitData.append('colors_json', JSON.stringify(formData.colors));
        } else if (key === 'sentiments' && formData.sentiments.length > 0) {
          submitData.append('sentiments_json', JSON.stringify(formData.sentiments));
        } else if (key === 'tags' && formData.tags.length > 0) {
          submitData.append('tags_json', JSON.stringify(formData.tags));
        } else if (formData[key] && key !== 'colors' && key !== 'sentiments' && key !== 'tags') {
          submitData.append(key, formData[key]);
        }
      });

      const resp = await fetch(`${BACKEND_URL}/api/stash`, {
        method: 'POST', credentials: 'include', headers: getAuthHeaders(), body: submitData
      });

      if (resp.ok) {
        onSuccess();
        onClose();
      } else {
        const error = await resp.clone().json();
        alert(error.detail || 'Failed to add item');
      }
    } catch (err) {
      console.error('Submit error:', err);
      alert('Failed to add item');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const allImages = [
    ...(lookupResult?.images || []),
  ];

  return (
    <div className="fixed inset-0 bg-cream z-[60] flex flex-col overflow-hidden">
      {/* Processing & Sentiment Dialogs */}
      {showProcessing && (
        <ProcessingDialog
          imagePreview={imagePreview}
          detectColors={detectColors}
          setDetectColors={setDetectColors}
          detectSentiments={detectSentiments}
          setDetectSentiments={setDetectSentiments}
          onProcess={handleProcessImage}
          onSkip={handleSkipProcessing}
          isProcessing={uploadingImage}
        />
      )}
      {showSentimentConfirm && (
        <SentimentDialog
          pendingSentiments={pendingSentiments}
          setPendingSentiments={setPendingSentiments}
          onConfirm={handleConfirmSentiments}
          onCancel={() => { setPendingSentiments([]); setShowSentimentConfirm(false); }}
        />
      )}

      {/* Lightbox */}
      {previewUrl && (
        <div
          className="fixed inset-0 bg-charcoal/80 flex items-center justify-center z-[80] p-4 cursor-pointer"
          onClick={() => setPreviewUrl(null)}
        >
          <div className="relative max-w-3xl w-full max-h-[85vh] flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
            <img src={previewUrl} alt="Preview" className="max-w-full max-h-[85vh] object-contain rounded-xl shadow-2xl" />
            <button onClick={() => setPreviewUrl(null)} className="absolute top-3 right-3 p-2 bg-white/90 rounded-full hover:bg-white transition-colors shadow-lg">
              <X size={18} />
            </button>
          </div>
        </div>
      )}

      {/* No Image Confirmation */}
      {showNoImageConfirm && (
        <div className="fixed inset-0 bg-charcoal/60 flex items-center justify-center z-[70] p-4">
          <div className="bg-white rounded-xl max-w-sm w-full shadow-2xl animate-in fade-in zoom-in duration-200 p-6">
            <h3 className="text-lg font-medium text-charcoal mb-2">Continue without an image?</h3>
            <p className="text-sm text-gray-soft mb-6">
              No product image is selected. You can still add the item and attach an image later.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowNoImageConfirm(false)}
                className="flex-1 py-2.5 border border-beige text-charcoal rounded-lg hover:bg-cream transition-colors text-sm"
                data-testid="no-image-go-back"
              >
                Go Back
              </button>
              <button
                onClick={_proceedToFormInternal}
                className="flex-1 py-2.5 bg-sage text-white rounded-lg hover:bg-sage-dark transition-colors text-sm font-medium"
                data-testid="no-image-continue"
              >
                Continue Anyway
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="bg-white border-b border-beige px-4 sm:px-6 py-4 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-3">
          {step !== 'input' && (
            <button
              onClick={() => setStep(step === 'form' ? 'results' : 'input')}
              className="p-2 text-gray-soft hover:text-charcoal rounded-lg hover:bg-cream transition-colors"
              data-testid="url-flow-back-btn"
            >
              <ArrowLeft size={20} />
            </button>
          )}
          <div>
            <h2 className="text-lg font-medium text-charcoal">Add by URL</h2>
            <p className="text-xs text-gray-soft">
              {step === 'input' && 'Paste a product page or image URL'}
              {step === 'results' && 'Product found — review details'}
              {step === 'form' && 'Review details and save to stash'}
            </p>
          </div>
        </div>
        <button
          onClick={onClose}
          className="p-2 text-gray-soft hover:text-charcoal rounded-lg hover:bg-cream transition-colors"
          data-testid="url-flow-close-btn"
        >
          <X size={20} />
        </button>
      </div>

      {/* Progress Bar */}
      <div className="bg-white px-4 sm:px-6 pb-3 flex-shrink-0">
        <div className="flex gap-2">
          {['input', 'results', 'form'].map((s, i) => (
            <div
              key={s}
              className={`h-1 flex-1 rounded-full transition-colors duration-300 ${
                ['input', 'results', 'form'].indexOf(step) >= i ? 'bg-sage' : 'bg-beige'
              }`}
            />
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6">

          {/* === STEP 1: URL INPUT === */}
          {step === 'input' && (
            <div className="space-y-6">
              <div className="text-center py-4">
                <div className="w-20 h-20 bg-sage/20 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Link size={36} className="text-sage" />
                </div>
                <h3 className="text-xl font-medium text-charcoal mb-2">Paste a URL</h3>
                <p className="text-sm text-gray-soft max-w-md mx-auto">
                  Paste a product page URL or a direct image link. We'll extract the product details and images automatically.
                </p>
              </div>

              {/* URL Input */}
              <div className="bg-white rounded-xl p-5 border border-beige">
                <label className="block text-sm font-medium text-charcoal mb-2">Product or Image URL</label>
                <div className="flex flex-col sm:flex-row gap-2">
                  <input
                    ref={urlInputRef}
                    type="text"
                    value={urlInput}
                    onChange={(e) => setUrlInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleLookup())}
                    placeholder="https://www.example.com/product..."
                    className="flex-1 px-4 py-3 border border-beige rounded-lg focus:border-sage focus:ring-2 focus:ring-sage/20 transition-all duration-200"
                    data-testid="url-input"
                  />
                  <button
                    type="button"
                    onClick={handleLookup}
                    disabled={lookupLoading || !urlInput.trim()}
                    className="w-full sm:w-auto px-6 py-3 bg-sage text-white rounded-lg hover:bg-sage-dark disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
                    data-testid="url-lookup-btn"
                  >
                    {lookupLoading ? <Loader className="animate-spin" size={20} /> : <Search size={20} />}
                    <span>Lookup</span>
                  </button>
                </div>
                <p className="text-xs text-gray-light mt-2">
                  Works with product pages, store listings, or direct image URLs (.jpg, .png, .webp)
                </p>
              </div>

              {/* Loading */}
              {lookupLoading && (
                <div className="text-center py-6">
                  <Loader className="animate-spin mx-auto text-sage mb-3" size={36} />
                  <p className="text-sm text-gray-soft">Fetching product details...</p>
                </div>
              )}

              {/* Error */}
              {lookupError && !lookupLoading && (
                <div className="bg-red-50 rounded-xl p-5">
                  <div className="flex items-center gap-2 text-red-600 mb-3">
                    <AlertCircle size={18} />
                    <span className="text-sm font-medium">{lookupError}</span>
                  </div>
                  <button
                    type="button"
                    onClick={skipToManualEntry}
                    className="w-full py-2 text-sm text-sage hover:text-sage-dark transition-colors"
                    data-testid="url-skip-to-manual"
                  >
                    Enter details manually instead
                  </button>
                </div>
              )}
            </div>
          )}

          {/* === STEP 2: RESULTS === */}
          {step === 'results' && lookupResult && (
            <div className="space-y-5">
              {/* Product Found Banner */}
              <div className="bg-green-50 rounded-xl p-5 border border-green-200">
                <div className="flex items-center gap-2 text-green-700 mb-1">
                  <Check size={20} />
                  <span className="font-medium">
                    {lookupResult.source === 'image' ? 'Image Identified' : 'Product Found'}
                  </span>
                </div>
                <p className="text-sm text-green-600 truncate">
                  {urlInput}
                </p>
              </div>

              {/* Product Preview */}
              <div className="bg-white rounded-xl p-5 border border-beige">
                <div className="flex items-start gap-4">
                  {imagePreview ? (
                    <img src={imagePreview} alt="Product" className="w-24 h-24 object-cover rounded-lg flex-shrink-0" />
                  ) : allImages.length > 0 ? (
                    <img src={allImages[0].url} alt="Product" className="w-24 h-24 object-cover rounded-lg flex-shrink-0" onError={(e) => { e.target.style.display = 'none'; }} />
                  ) : (
                    <div className="w-24 h-24 bg-beige rounded-lg flex items-center justify-center flex-shrink-0">
                      <ImageIcon size={32} className="text-gray-light" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    {formData.name && (
                      <h4 className="text-base font-medium text-charcoal mb-1">{formData.name}</h4>
                    )}
                    {formData.brand && (
                      <p className="text-sm text-gray-soft mb-2">{formData.brand}</p>
                    )}
                    {formData.description && (
                      <p className="text-xs text-gray-soft line-clamp-3">{formData.description}</p>
                    )}
                    {formData.tags?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {formData.tags.slice(0, 5).map((tag, idx) => (
                          <span key={idx} className="text-xs px-2 py-0.5 bg-beige text-gray-soft rounded-full">{tag}</span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Image Selection (if multiple images scraped) */}
              {allImages.length > 0 && !lookupResult.uploaded_image_url && (
                <div className="bg-white rounded-xl p-5 border border-beige">
                  <p className="text-sm font-medium text-charcoal mb-3">Select a product image</p>
                  <div className="grid grid-cols-3 gap-2">
                    {allImages.slice(0, 9).map((img, idx) => (
                      <div key={idx} className="relative group">
                        <button
                          type="button"
                          onClick={() => handleSelectImage(img)}
                          className={`relative w-full aspect-square rounded-lg overflow-hidden bg-gray-100 ${
                            selectedImage?.url === img.url ? 'ring-2 ring-sage ring-offset-2' : 'hover:ring-2 hover:ring-sage/50'
                          } transition-all`}
                          data-testid={`url-img-option-${idx}`}
                        >
                          <img
                            src={img.url}
                            alt={img.alt || ''}
                            className="w-full h-full object-cover"
                            onError={(e) => { e.target.parentElement.parentElement.style.display = 'none'; }}
                          />
                          {selectedImage?.url === img.url && (
                            <div className="absolute top-1.5 right-1.5 w-6 h-6 bg-sage rounded-full flex items-center justify-center shadow-sm">
                              <Check size={14} className="text-white" />
                            </div>
                          )}
                        </button>
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); setPreviewUrl(img.url); }}
                          className="absolute bottom-1.5 right-1.5 p-1.5 bg-white/90 rounded-lg shadow-sm hover:bg-white transition-all opacity-80 sm:opacity-0 sm:group-hover:opacity-100"
                          data-testid={`url-img-preview-${idx}`}
                        >
                          <Maximize2 size={14} className="text-charcoal" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => { setLookupResult(null); setUrlInput(''); setSelectedImage(null); setFormData(INITIAL_STASH_FORM); setImagePreview(null); setStep('input'); }}
                  className="flex-1 py-3 border border-beige text-gray-soft rounded-xl hover:border-sage hover:text-sage transition-colors text-sm"
                  data-testid="url-try-again-btn"
                >
                  Try Another URL
                </button>
                <button
                  type="button"
                  onClick={proceedToForm}
                  disabled={uploadingImage}
                  className="flex-1 py-3 bg-sage text-white rounded-xl hover:bg-sage-dark transition-colors text-sm font-medium flex items-center justify-center gap-2"
                  data-testid="url-proceed-btn"
                >
                  {uploadingImage ? (
                    <Loader size={16} className="animate-spin" />
                  ) : (
                    <span>Continue to Review</span>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* === STEP 3: FORM === */}
          {step === 'form' && (
            <EditItemForm
              mode="create"
              initialFormData={formData}
              onSubmit={async (finalData) => {
                const submitData = new FormData();
                Object.keys(finalData).forEach(key => {
                  if (key === 'colors' && finalData.colors.length > 0) {
                    submitData.append('colors_json', JSON.stringify(finalData.colors));
                  } else if (key === 'sentiments' && finalData.sentiments.length > 0) {
                    submitData.append('sentiments_json', JSON.stringify(finalData.sentiments));
                  } else if (key === 'tags' && finalData.tags.length > 0) {
                    submitData.append('tags_json', JSON.stringify(finalData.tags));
                  } else if (finalData[key] && !['colors', 'sentiments', 'tags'].includes(key)) {
                    submitData.append(key, finalData[key]);
                  }
                });
                const resp = await fetch(`${BACKEND_URL}/api/stash`, {
                  method: 'POST', credentials: 'include', headers: getAuthHeaders(), body: submitData,
                });
                if (!resp.ok) {
                  const error = await resp.clone().json();
                  throw new Error(error.detail || 'Failed to add item');
                }
                onSuccess();
                onClose();
              }}
              onCancel={onClose}
              isFullScreen={false}
              submitLabel="Add to Stash"
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default URLLookupFlow;
